import { useMutation } from '@tanstack/react-query'

import dialogStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { isApiError } from '@/api'
import { syncDomainsToWorkos } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'

interface SyncWorkosDialogProps {
  isOpen: boolean
  enterpriseId: string
  enterpriseName: string
  onSuccess: (workos_synced: boolean) => void
  onCancel: () => void
}

const SyncWorkosDialog = ({ isOpen, enterpriseId, enterpriseName, onSuccess, onCancel }: SyncWorkosDialogProps) => {
  const { updateToast } = useToast()

  const mutation = useMutation({
    mutationFn: () => syncDomainsToWorkos(enterpriseId),
    onSuccess: (response) => {
      if (response.data.workos_synced) {
        updateToast(response.data.message ?? 'Domains synced to WorkOS successfully', 'success')
      } else {
        updateToast(response.data.message ?? 'Failed to sync domains to WorkOS', 'error')
      }
      onSuccess(response.data.workos_synced)
    },
    onError: (err) => {
      if (isApiError(err)) {
        updateToast(err.detail || 'Failed to sync to WorkOS', 'error')
      } else {
        updateToast('Failed to sync to WorkOS', 'error')
      }
    },
  })

  return (
    <DialogWrapper
      isOpen={isOpen}
      title="Sync domains to WorkOS"
      subtitle={`This will sync all active (non-deleted) domains for "${enterpriseName}" to WorkOS. Use this to retry if a previous sync failed.`}
      close={onCancel}
    >
      <div className={dialogStyles.dialogContent}>
        <div className={dialogStyles.dialogActions}>
          <Button title="Cancel" onClick={onCancel} type="light" />
          <Button
            title="Sync to WorkOS"
            onClick={() => mutation.mutate()}
            type="dark"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default SyncWorkosDialog
